const policy = `
<h1>{{BUSINESS}} Privacy Policy</h1><br>

<p><strong>Last updated:</strong> {{DATE}}</p><br>
<p>This Privacy Policy describes how {{BUSINESS}} (the "business", "we", "us", or "our") collects, uses, and discloses your personal information when you {{#if WEBSITE}}visit our website, {{/if}}join our waiting list, book a turn in our queue, submit a review, or otherwise interact with our services{{#if WEBSITE}} through <a href="{{WEBSITE}}">{{WEBSITE}}</a>{{/if}} (collectively, the "Services").</p><br>
<p>For purposes of this Privacy Policy, "you" and "your" means you as the customer or individual whose information we collect.</p><br>
<p>Please read this Privacy Policy carefully. By using our Services, joining our waiting list, or submitting a review, you agree to the collection, use, and disclosure of your information as described in this Privacy Policy.</p><br><br>

<h2>Legal Compliance</h2><br>
<p>This Privacy Policy is designed to comply with applicable privacy and data protection laws in {{COUNTRY}} where we operate. {{BUSINESS}} is committed to protecting your personal information in accordance with applicable legal requirements.</p><br><br>

<h2>What Personal Information We Collect</h2><br>
<p>We collect personal information when you interact with our Services, including:</p><br><br>

<h3>When You Join Our Waiting List or Queue</h3><br>
<ul>
  <li><strong>Contact details:</strong> Your name, mobile phone number, and email address (if provided)</li>
  <li><strong>Queue information:</strong> Your position in the queue, estimated wait time, and preferences</li>
  <li><strong>Communication preferences:</strong> Whether you consent to receive SMS notifications about your queue status</li>
</ul><br><br>

<h3>When You Submit a Review</h3><br>
<ul>
  <li><strong>Contact details:</strong> Your name and email address (if provided)</li>
  <li><strong>Review content:</strong> Your review, rating, and any comments you provide</li>
  <li><strong>Service details:</strong> Information about the service or experience you're reviewing</li>
</ul><br><br>

<h3>Other Information We May Collect</h3><br>
<ul>
  <li><strong>Customer support:</strong> Messages you send when contacting us for help</li>
  {{#if WEBSITE}}<li><strong>Website usage:</strong> Information about how you interact with our website through cookies and similar technologies</li>{{/if}}
</ul><br><br>

<h2>How We Use Your Personal Information</h2><br>
<p>We use your personal information for the following purposes:</p><br>
<ul>
  <li><strong>Queue Management:</strong> To manage your position in our waiting list and notify you when it's your turn</li>
  <li><strong>SMS Notifications:</strong> To send you text messages about your queue status, estimated wait times, and when your turn is ready (only with your consent where required by law)</li>
  <li><strong>Review Display:</strong> To display your reviews on our website, social media, or other platforms to help other customers</li>
  <li><strong>Customer Service:</strong> To respond to your enquiries and provide support</li>
  <li><strong>Business Improvement:</strong> To understand customer preferences and improve our services</li>
  <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
</ul><br><br>

<h2>SMS Communications</h2><br>
<p>If you provide your mobile phone number and consent to SMS notifications, we will send you text messages about:</p><br>
<ul>
  <li>Your current position in the queue</li>
  <li>Estimated wait times</li>
  <li>When your turn is ready</li>
  <li>Important updates about our services</li>
</ul><br>
<p>You can opt out of SMS communications at any time by replying "STOP" to any message or contacting us using the details below. Standard messaging charges may apply.</p><br><br>

<h2>How We Share Your Personal Information</h2><br>
<p>We may share your personal information with:</p><br>
<ul>
  <li><strong>Service providers:</strong> Third-party providers who help us operate our queue management and messaging systems</li>
  <li><strong>Review platforms:</strong> Your reviews may be displayed on our website, social media platforms, or other review platforms</li>
  <li><strong>Legal authorities:</strong> When required by law or to protect our rights and safety</li>
  <li><strong>Business partners:</strong> With your consent, we may share information with trusted business partners</li>
</ul><br><br>

<h2>International Data Transfers</h2><br>
<p>Some of our service providers may be located in different countries or outside {{COUNTRY}}. Where we transfer your personal information internationally, we will take appropriate steps to ensure your information is protected in accordance with applicable privacy laws.</p><br><br>

{{#if WEBSITE}}<h2>Cookies and Website Usage</h2><br>
<p>Our website may use cookies and similar technologies to improve your experience. You can control cookies through your browser settings, but this may affect how our website functions.</p><br><br>{{/if}}

<h2>Review Display and Public Information</h2><br>
<p>When you submit a review, please be aware that:</p><br>
<ul>
  <li>Your review and name may be displayed publicly on our website, social media, or review platforms</li>
  <li>Reviews may be visible to anyone and may appear in search engine results</li>
  <li>We may use your reviews in our marketing materials</li>
  <li>Once published, reviews may be difficult to remove completely from the internet</li>
</ul><br><br>

<h2>Data Security and Retention</h2><br>
<p>We implement reasonable security measures to protect your personal information from unauthorised access, use, or disclosure. We retain your information only for as long as necessary to provide our services or as required by applicable law.</p><br>
<p>Queue information is typically deleted after your service is completed, but we may retain contact details for future bookings if you consent.</p><br><br>

<h2>Data Breaches</h2><br>
<p>In the event of a data breach that may affect your personal information, we will take appropriate action in accordance with applicable laws in {{COUNTRY}}, which may include notifying relevant authorities and affected individuals.</p><br><br>

<h2>Your Rights and Choices</h2><br>
<p>Depending on applicable laws in {{COUNTRY}}, you may have rights regarding your personal information, which may include:</p><br>
<ul>
  <li><strong>Access:</strong> The right to request access to the personal information we hold about you</li>
  <li><strong>Correction:</strong> The right to request correction of inaccurate information</li>
  <li><strong>Deletion:</strong> The right to request deletion of your personal information in certain circumstances</li>
  <li><strong>Opt-out:</strong> The right to opt out of SMS notifications and marketing communications</li>
  <li><strong>Review removal:</strong> The right to request removal of your reviews (subject to our policies and applicable laws)</li>
  <li><strong>Complaints:</strong> The right to make a complaint about our handling of your personal information</li>
</ul><br>
<p>To exercise these rights, please contact us using the details provided below.</p><br><br>

<h2>Children's Privacy</h2><br>
<p>Our services are not intended for children under the age of majority in {{COUNTRY}}. If we become aware that we have collected personal information from a child without appropriate consent, we will take steps to delete it promptly.</p><br><br>

<h2>Complaints and Privacy Concerns</h2><br>
<p>If you have complaints or concerns about how we handle your personal information, please contact us using the details below. We will investigate your concern and respond in accordance with applicable laws.</p><br>
<p>If you are not satisfied with our response, you may have the right to lodge a complaint with the relevant privacy or data protection authority in {{COUNTRY}}.</p><br><br>

<h2>Changes to This Privacy Policy</h2><br>
<p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. {{#if WEBSITE}}We will post the revised Privacy Policy on our website and update the "Last updated" date.{{else}}We will notify you of any material changes and update the "Last updated" date.{{/if}} Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.</p><br><br>

<h2>Contact Us</h2><br>
<p>For any questions about this Privacy Policy, to exercise your rights, or to opt out of communications, contact us at:</p><br>
<p><strong>Email:</strong> {{EMAIL}}</p>
<p><strong>Address:</strong> {{ADDRESS}}</p>
<p><strong>Country:</strong> {{COUNTRY}}</p><br>
<p>We will respond to your inquiry in accordance with applicable privacy laws in {{COUNTRY}}.</p>
`;

export default policy;
